<script setup>
import { computed, watch, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import OverviewLineChart from '@/components/overview/OverviewLineChart.vue'
import OverViewTable from '@/components/overview/OverviewTable.vue'
import TabTemplate from '@/components/common/TabTemplate.vue'

const store = useStore()

const topHeight = inject('topHeight')

const ignoreHeight = computed(() => {
  return (topHeight?.value ?? 0) + 'px'
})

const selectedStabSchematisationStageId = computed(
  () => store.state.dikeProfiles.selectedStabSchematisationStageId
)
const overviewLoading = computed(() => store.state.overview.loading)
const stabSchematisation = computed(() => store.state.stabSchematisation.currentItem)
const currentOverViewItem = computed(() => store.state.stabSchematisation.currentOverviewItem)
const stabSchematisationItems = computed(() => store.state.stabSchematisation.items)
const stabSchematisationStage = computed(() => store.state.stabSchematisationStage.currentItem)
const loadingSoilType = computed(() => store.state.soilType.loading)
const getLoadingById = (id) => store.getters['overview/getLoadingById'](id)
const getStabSchematisationStageById = (id) =>
  store.getters['stabSchematisationStage/getStabSchematisationStageById'](id)
const getSchemaLoadingById = (id) => store.getters['stabSchematisationStage/getLoadingById'](id)

const overViewItemId = computed(() => currentOverViewItem.value?.id)
const latestDoneSchematisationStage = computed(
  () => currentOverViewItem.value?.latest_done_schematisation_stage
)

const loading = computed(() => {
  if (selectedStabSchematisationStageId.value) {
    return (
      loadingSoilType.value ||
      overviewLoading.value ||
      getSchemaLoadingById(selectedStabSchematisationStageId.value) ||
      getLoadingById(selectedStabSchematisationStageId.value)
    )
  }
  return false
})

const fetchOverviewData = (payload) => store.dispatch('overview/fetchOverviewData', payload)
const toggleOverviewLoading = (payload) => store.dispatch('overview/toggleLoading', payload)
const setSelectedStabSchematisationStageId = (payload) =>
  store.dispatch('dikeProfiles/setSelectedStabSchematisationStageId', payload)
const fetchStabSchematisationOverview = (payload) =>
  store.dispatch('stabSchematisationStage/fetchItemOverview', payload)
const setCurrentOverviewSchematisation = (payload) =>
  store.dispatch('stabSchematisation/setCurrentOverviewItem', payload)

const fetchRelatedData = async () => {
  if (selectedStabSchematisationStageId.value) {
    await fetchStabSchematisationOverview(selectedStabSchematisationStageId.value)
    const stabSchematisationStageObj = getStabSchematisationStageById(
      selectedStabSchematisationStageId.value
    )
    await fetchOverviewData(stabSchematisationStageObj)
  }
}

watch(overViewItemId, async () => {
  const stabSchema = getStabSchematisationStageById(latestDoneSchematisationStage.value)
  if (currentOverViewItem.value && stabSchema) {
    await fetchOverviewData(stabSchema)
    await setSelectedStabSchematisationStageId(latestDoneSchematisationStage.value)
  }
})

watch(selectedStabSchematisationStageId, async () => {
  toggleOverviewLoading(true)
  await fetchRelatedData()
  toggleOverviewLoading(false)
})

onMounted(async () => {
  let latest_done_schematisation_stage = null
  if (selectedStabSchematisationStageId.value) {
    latest_done_schematisation_stage = selectedStabSchematisationStageId.value
  } else if (
    stabSchematisation.value &&
    stabSchematisation.value.latest_done_schematisation_stage
  ) {
    latest_done_schematisation_stage = stabSchematisation.value.latest_done_schematisation_stage
  }
  toggleOverviewLoading(true)
  setSelectedStabSchematisationStageId(latest_done_schematisation_stage)
  await setCurrentOverviewSchematisation(stabSchematisation.value)
  await fetchRelatedData()
  toggleOverviewLoading(false)
})
</script>

<template>
  <TabTemplate :cols="5">
    <template v-slot:chart>
      <template v-if="currentOverViewItem?.dikesection_id === stabSchematisation?.dikesection_id">
        <template v-if="selectedStabSchematisationStageId > 0">
          <v-progress-linear v-if="loading" color="primary" indeterminate></v-progress-linear>
          <OverviewLineChart
            v-if="!loading"
            :ignore-height="ignoreHeight"
            :stab-schematisation-stage="stabSchematisationStage"
          />
        </template>
        <div v-else class="fill-height center-align">
          <span> No stages marked as done for this schematisation. </span>
        </div>
      </template>
      <div v-else class="fill-height center-align">
        <span> Please choose a schematisation in the left sidebar to view the overview. </span>
      </div>
    </template>
    <template v-slot:content>
      <v-progress-linear
        v-if="!stabSchematisationItems.length"
        color="primary"
        indeterminate
      ></v-progress-linear>
      <OverViewTable v-else />
    </template>
  </TabTemplate>
</template>

<style scoped>
.center-align {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
